import { createStyles } from 'antd-style';

import {
  ThemeColors,
  ThemeFont,
  ThemeSpaces,
  hexToRgbaWithAlpha,
} from '../../../theme';
import { blindedBlock } from '../shipmentDetailShared.styles';
import { defBtnDisabled, spaceBetween } from '../../../styles/mixins';
import { FA_SELECTOR } from '../../../styles/variables';

export const useShipmentHistoryAndDocsStyles = createStyles(
  ({ css, cx, token }) => {
    const fileUploadItemPreviewDocumentName = cx(
      css({
        flex: 1,
        fontSize: token.fontSizeLG,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      })
    );
    const tableCellCommon = {
      fontSize: token.fontSize,
      fontWeight: 500,
      borderWidth: 0,
      backgroundColor: token.colorWhite,
    };

    return {
      tableRow: {
        height: 40,
        border: `1px solid ${ThemeColors.COLOR_GREY9_8}`,
      },
      documentsTableCellDate: {
        ...tableCellCommon,
        width: 90,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: ThemeColors.COLOR_GREY3,
        padding: 0,
      },
      documentsTableCellLink: {
        ...tableCellCommon,
        flex: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: ThemeColors.COLOR_GREY3,
        padding: 0,
      },
      documentsInlineLink: {
        color: ThemeColors.COLOR_BLUE3,

        '> span': {
          textDecoration: 'underline',
        },
        [FA_SELECTOR]: {
          textDecoration: 'none',
        },
      },
      documentsUploadButtonWrapper: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: 0,
        width: 85,
      },
      documentsUploadButton: {
        width: '100%',
        color: ThemeColors.COLOR_RED,
        ...defBtnDisabled({
          color: hexToRgbaWithAlpha(ThemeColors.COLOR_GREY3_5, 0.5),
        }),
      },
      documentsUploadDialog: {
        '.ant-modal-content': {
          minHeight: 230,

          [`@media (min-width: ${token.screenMDMin}px)`]: {
            width: 520,
          },
          [`@media (max-width: ${token.screenSMMax}px)`]: {
            width: 280,
          },
        },

        '.ant-modal-body': {
          display: 'block',

          [`@media (min-width: ${token.screenMDMin}px)`]: {
            padding: ThemeSpaces.SPACE_NORM_20,
          },
          [`@media (max-width: ${token.screenSMMax}px)`]: {
            padding: `${ThemeSpaces.SPACE_NORM_20}px ${ThemeSpaces.SPACE_NORM}px`,
          },
        },

        '.SimpleDialog-Icon': {
          marginTop: -6,

          '&::before': {
            top: 11,
            bottom: 4,
            right: 13,
            left: 10,
            borderRadius: 0,
          },
        },
      },
      documentsUploadDialogButtons: {
        display: 'flex',
        justifyContent: 'center',

        '> .ant-btn': {
          width: '100%',
          maxWidth: 150,

          [`@media (min-width: ${token.screenLGMin}px)`]: {
            ...spaceBetween(ThemeSpaces.SPACE_NORM_20, 'horizontal'),
          },
          [`@media (max-width: ${token.screenMDMax}px)`]: {
            ...spaceBetween(ThemeSpaces.SPACE_NORM, 'horizontal'),
          },
        },
      },
      trackingTableHead: {
        height: 24,
        backgroundColor: hexToRgbaWithAlpha(ThemeColors.COLOR_BLUE4, 0.5),
        color: token.colorWhite,
        fontSize: token.fontSizeSM,
        fontWeight: 500,
      },
      trackingTableCellTime: {
        ...tableCellCommon,
        color: ThemeColors.COLOR_GREY3,
        width: 70,
      },
      trackingTableCellType: {
        ...tableCellCommon,
        color: ThemeColors.COLOR_GREY3,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      trackingHistoryTableRowIsLocal: {
        '&&': {
          alignItems: 'flex-start',
          paddingTop: 6,
        },
      },
      trackingHistoryLocation: {
        fontSize: ThemeFont.FONT_SIZE_SM_MINUS,
        fontWeight: 400,
      },
      trackingHistoryLocationIsHub: {
        marginLeft: 4,
        color: ThemeColors.COLOR_BLUE3,
      },
      activityCard: {
        '&&&': {
          '.SimpleCard-Header': {
            height: 'auto',
          },
        },
      },
      blindedActivity: {
        ...blindedBlock(),
        padding: `${ThemeSpaces.SPACE_MD}px 0`,
        justifyContent: 'center',
      },
      tooltipArrowAtLeft: {
        '&& .ant-tooltip-inner': {
          transform: 'translateX(50%)',
          left: '-24px',

          '&::before, &::after': {
            left: '24px',
          },
        },
      },
      hubTooltip: {
        '&& .ant-tooltip-inner': {
          borderRadius: token.borderRadiusSM,
          lineHeight: '22px',
          fontSize: token.fontSize,
          padding: `${token.paddingXS}px ${token.paddingSM}px`,
          boxShadow: `0px 2px 6px 0px ${hexToRgbaWithAlpha(
            ThemeColors.COLOR_GREY_BLUISH3,
            0.65
          )}`,
        },
      },
      fileUploadItemPreview: {
        display: 'flex',
        alignItems: 'center',
      },
      fileUploadItemPreviewIcon: {
        width: ThemeSpaces.SPACE_NORM_20,

        '.ThreeDotsSpinner': {
          marginLeft: 0,
        },
      },
      fileUploadItemPreviewIconSuccess: {
        color: ThemeColors.COLOR_LIGHT_GREEN2,
      },
      fileUploadItemPreviewIconError: {
        color: ThemeColors.COLOR_RED,
      },
      fileUploadItemPreviewDocumentName,
      fileUploadItemPreviewHasError: {
        [`.${fileUploadItemPreviewDocumentName}`]: {
          color: ThemeColors.COLOR_RED,
        },
      },
      fileUploadItemPreviewWithSelect: {
        '> *': {
          marginRight: ThemeSpaces.SPACE_NORM,
        },
        '> *:first-child, > *:last-child': {
          marginRight: 0,
        },
      },
      fileUploadItemPreviewError: {
        color: ThemeColors.COLOR_RED,
        lineHeight: 1,
        paddingLeft: ThemeSpaces.SPACE_NORM_20,
      },
    };
  }
);
